<template>
  <MultiSelect
    ref="multiselect"
    v-model="control.value"
    :options="options"
    :loading="control.customStyles?.loading || false"
    :disabled="control.disabled"
    :showToggleAll="false"
    optionLabel="label"
    :placeholder="control.customStyles?.placeholder || 'Selecione as opções'"
    :panelStyle="{ width: panelWidthPx + 'px' }"
    :emptyMessage="'Nenhum resultado encontrado'"
    :class="`w-full mr-4${control.valid ? '' : ' form-control--error'}`"
    :virtualScrollerOptions="scrollerOptions"
    @before-show="onBeforeShow"
    @update:modelValue="onSelect"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    <!-- Busca própria: o `filter` nativo roda o FilterService sobre o array
         local, que em modo lazy é quase todo `undefined`. -->
    <template #header>
      <div class="lazy-multiselect-header">
        <InputText
          v-model="search"
          class="w-full"
          :placeholder="
            control.customStyles?.filterPlaceholder || 'Pesquisar...'
          "
          @update:model-value="onSearchInput"
        />
      </div>
    </template>

    <!-- Os rótulos saem do próprio `control.value`: os itens escolhidos podem
         não estar na faixa carregada de `options`. -->
    <template #value="slot">
      <span v-if="slot.value?.length">{{ labelsOf(slot.value) }}</span>
      <span v-else>{{ slot.placeholder }}</span>
    </template>

    <template
      v-if="control.customStyles?.propetyFilter?.[0]"
      #option="slotProps"
    >
      <div class="flex flex-col">
        <span style="white-space: normal">{{
          slotProps.option?.value?.[control.customStyles?.propetyFilter[0]]
        }}</span>
        <span
          v-if="control.customStyles?.propetyFilter[1]"
          class="ml-2"
          style="font-size: 10px; white-space: normal"
          >{{
            slotProps.option?.value?.[control.customStyles?.propetyFilter[1]]
          }}</span
        >
      </div>
    </template>

    <!-- Renderizado no lugar dos itens da faixa que ainda está sendo buscada. -->
    <template #loader>
      <div class="lazy-multiselect-loader" :style="{ height: itemSize + 'px' }">
        <Skeleton width="60%" height="1rem" />
      </div>
    </template>

    <template #empty>
      <div style="padding: 0.5rem 1rem">
        <span>{{ loading ? "Carregando..." : "Nenhum resultado encontrado" }}</span>
      </div>
    </template>
  </MultiSelect>
</template>

<script>
/**
 * MultiSelect com virtual scroll lazy (PrimeVue 3.47). Mesma ideia do
 * LazyDropdown; o campo opta declarando `customStyles.lazy`:
 *
 *   customStyles: {
 *     lazy: {
 *       // devolve { items, total } para a faixa pedida
 *       fetch: ({ first, last, search }) => Promise<{ items, total }>,
 *       itemSize: 38,   // opcional, altura da linha em px
 *       pageSize: 50,   // opcional, mínimo de itens por busca
 *     },
 *   }
 */
export default {
  name: "LazyMultiSelect",
  emits: ["focus", "blur", "change"],
  props: {
    control: { type: Object, required: true },
  },
  data() {
    return {
      options: [],
      total: 0,
      search: "",
      loading: false,
      loaded: false,
      panelWidthPx: 0,
    };
  },
  computed: {
    itemSize() {
      return this.control.customStyles?.lazy?.itemSize || 38;
    },
    pageSize() {
      return this.control.customStyles?.lazy?.pageSize || 50;
    },
    /**
     * O modo lazy fica sempre ligado, mesmo quando o total cabe numa página.
     *
     * Ligar/desligar conforme o total trava o painel: no VirtualScroller do
     * PrimeVue (3.47 e 3.53) o watcher que espelha a prop `loading` em
     * `d_loading` só roda com `lazy: true`. Quando uma busca reduz o total a
     * <= pageSize, as props lazy somem no mesmo flush em que `loading` volta a
     * `false`, o watcher é ignorado e `d_loading` fica preso em `true` —
     * `loadedItems` passa a devolver `[]` e a lista fica em branco até fechar
     * e reabrir.
     *
     * Com o lazy sempre ligado nada disso acontece, e o custo é nulo: numa
     * lista já inteira em memória o `onLazyLoad` cai no early return de
     * `isRangeLoaded`.
     */
    scrollerOptions() {
      return {
        itemSize: this.itemSize,
        lazy: true,
        showLoader: true,
        // Sem `step`, o isPageChanged() do VirtualScroller refaz a busca a
        // cada poucas linhas roladas.
        step: this.pageSize,
        loading: this.loading,
        onLazyLoad: this.onLazyLoad,
      };
    },
  },
  created() {
    // Fora do `data`: serve só para descartar resposta atrasada, não renderiza.
    this.requestId = 0;
    let timer = null;
    this.onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.reload(), 300);
    };
    this.clearSearchTimer = () => clearTimeout(timer);
  },
  beforeUnmount() {
    this.clearSearchTimer?.();
  },
  methods: {
    async onBeforeShow() {
      this.panelWidthPx = this.$refs.multiselect?.$el?.offsetWidth || 0;
      // Só busca na primeira abertura.
      if (!this.loaded) await this.reload();
    },
    async reload() {
      // Com uma tabela grande a consulta passa dos 300ms do debounce, então
      // duas buscas se sobrepõem: só a última pode escrever no estado.
      const requestId = ++this.requestId;
      this.loading = true;
      try {
        const { items, total } = await this.fetch(0, this.pageSize);
        if (requestId !== this.requestId) return;
        this.total = total;
        // O array dimensiona a barra de rolagem e marca o fim da lista.
        const next = new Array(total);
        this.fill(next, 0, items);
        this.options = next;
        this.loaded = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    /** `first`/`last` já vêm alinhados a `step`: nada a arredondar aqui. */
    async onLazyLoad({ first, last }) {
      if (!this.loaded) return;
      const start = Math.max(first, 0);
      const end = Math.min(last, this.total);
      if (this.isRangeLoaded(start, end)) return;
      // Um `reload()` no meio do caminho troca o array e o termo de busca:
      // estes índices não valem mais nada.
      const requestId = this.requestId;
      this.loading = true;
      try {
        const { items } = await this.fetch(start, end);
        if (requestId !== this.requestId) return;
        // Escrita por índice: reativa no Vue 3, sem copiar o array inteiro.
        this.fill(this.options, start, items);
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    fetch(first, last) {
      return this.control.customStyles.lazy.fetch({
        first,
        last,
        search: this.search,
      });
    },
    fill(target, first, items) {
      items.forEach((item, i) => {
        target[first + i] = item;
      });
    },
    isRangeLoaded(first, last) {
      for (let i = first; i < last; i++) {
        if (this.options[i] === undefined) return false;
      }
      return true;
    },
    /** O `$separator` é a convenção do `propetyFilter`: o que vem depois só
     * serve para a busca, não é exibido. */
    labelsOf(values) {
      return (values || [])
        .map((item) =>
          typeof item?.label === "string" ? item.label.split("$separator")[0] : ""
        )
        .filter(Boolean)
        .join(", ");
    },
    onSelect(value) {
      this.control.value = value;
      this.$emit("change", value);
    },
  },
};
</script>

<style>
.lazy-multiselect-header {
  padding: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}
.lazy-multiselect-loader {
  display: flex;
  align-items: center;
  padding: 0 1rem;
}
</style>
